import { MdxJsxFlowElement } from 'mdast-util-mdx-jsx'

type Styles = Record<string, string>
type AttributeSanitizer = (value: string) => string | Styles | null
export type AttributeSanitizerMap = Map<
	// attribute name
	string,
	AttributeSanitizer
>
export type HTMLAttributes = Record<string, string | Styles>

/**
 * sanitizeHTMLAttributes - returns an object containing html attributes whose values have been sanitized
 *
 * @param {MdxJsxFlowElement} node - the markdown node which contains the attributes to sanitize
 * @param {AttributeSanitizerMap} attributeSanitizers - a mapping of attribute names to sanitizers for those attributes
 *
 * @returns {HTMLAttributes}
 */
export function sanitizeHTMLAttributes(
	node: MdxJsxFlowElement,
	attributeSanitizers: AttributeSanitizerMap
): HTMLAttributes {
	const htmlAttributes: HTMLAttributes = {}
	for (const attribute of node.attributes) {
		if (
			attribute.type !== 'mdxJsxAttribute' ||
			typeof attribute.value !== 'string'
		) {
			continue
		}
		const sanitizedValue = attributeSanitizers.get(attribute.name)?.(
			attribute.value
		)
		if (sanitizedValue == null) {
			continue
		}

		htmlAttributes[attribute.name] = sanitizedValue
	}

	return htmlAttributes
}

const CSS_COLOR_NAMES = new Set([
	'black',
	'silver',
	'gray',
	'white',
	'maroon',
	'red',
	'purple',
	'fuchsia',
	'green',
	'lime',
	'olive',
	'yellow',
	'navy',
	'blue',
	'teal',
	'aqua',
	'aliceblue',
	'antiquewhite',
	'aquamarine',
	'azure',
	'beige',
	'bisque',
	'blanchedalmond',
	'blueviolet',
	'brown',
	'burlywood',
	'cadetblue',
	'chartreuse',
	'chocolate',
	'coral',
	'cornflowerblue',
	'cornsilk',
	'crimson',
	'cyan',
	'darkblue',
	'darkcyan',
	'darkgoldenrod',
	'darkgray',
	'darkgreen',
	'darkgrey',
	'darkkhaki',
	'darkmagenta',
	'darkolivegreen',
	'darkorange',
	'darkorchid',
	'darkred',
	'darksalmon',
	'darkseagreen',
	'darkslateblue',
	'darkslategray',
	'darkslategrey',
	'darkturquoise',
	'darkviolet',
	'deeppink',
	'deepskyblue',
	'dimgray',
	'dimgrey',
	'dodgerblue',
	'firebrick',
	'floralwhite',
	'forestgreen',
	'gainsboro',
	'ghostwhite',
	'gold',
	'goldenrod',
	'greenyellow',
	'grey',
	'honeydew',
	'hotpink',
	'indianred',
	'indigo',
	'ivory',
	'khaki',
	'lavender',
	'lavenderblush',
	'lawngreen',
	'lemonchiffon',
	'lightblue',
	'lightcoral',
	'lightcyan',
	'lightgoldenrodyellow',
	'lightgray',
	'lightgreen',
	'lightgrey',
	'lightpink',
	'lightsalmon',
	'lightseagreen',
	'lightskyblue',
	'lightslategray',
	'lightslategrey',
	'lightsteelblue',
	'lightyellow',
	'limegreen',
	'linen',
	'magenta',
	'mediumaquamarine',
	'mediumblue',
	'mediumorchid',
	'mediumpurple',
	'mediumseagreen',
	'mediumslateblue',
	'mediumspringgreen',
	'mediumturquoise',
	'mediumvioletred',
	'midnightblue',
	'mintcream',
	'mistyrose',
	'moccasin',
	'navajowhite',
	'oldlace',
	'olivedrab',
	'orange',
	'orangered',
	'orchid',
	'palegoldenrod',
	'palegreen',
	'paleturquoise',
	'palevioletred',
	'papayawhip',
	'peachpuff',
	'peru',
	'pink',
	'plum',
	'powderblue',
	'rosybrown',
	'royalblue',
	'saddlebrown',
	'salmon',
	'sandybrown',
	'seagreen',
	'seashell',
	'sienna',
	'skyblue',
	'slateblue',
	'slategray',
	'slategrey',
	'snow',
	'springgreen',
	'steelblue',
	'tan',
	'thistle',
	'tomato',
	'turquoise',
	'violet',
	'wheat',
	'whitesmoke',
	'yellowgreen',
])

// sanitizes css styles
const STYLE_SANITIZERS: Map<string, (value: string) => string | null> = new Map(
	Object.entries({
		color: (value: string): null | string => {
			const normalizedValue = value.toLowerCase()

			if (normalizedValue.match(/^#(\d{3}|\d{6}|\d{8})$/)) {
				// hex string
				return normalizedValue
			}

			if (CSS_COLOR_NAMES.has(normalizedValue)) {
				return normalizedValue
			}
			return null
		},
	})
)

// sanitizers for html attributes
export const SANITIZERS = {
	imageSrc: (src: string) => src,
	href: (href: string) => href,
	cite: (cite: string) => cite,
	style: (style: string): Styles => {
		const sanitizedStyles: Styles = {}
		for (const statement of style.split(';')) {
			const statementParts = statement.split(':')
			if (statementParts.length !== 2) {
				continue
			}
			const name = statementParts[0]?.trim()
			const value = statementParts[1]?.trim()
			if (!name || !value) {
				continue
			}

			const sanitizedValue = STYLE_SANITIZERS.get(name)?.(value)
			if (sanitizedValue == null) {
				continue
			}

			sanitizedStyles[name] = sanitizedValue
		}

		return sanitizedStyles
	},
}

export const DEFAULT_SANITIZER: AttributeSanitizerMap = new Map(
	Object.entries({
		style: SANITIZERS.style,
	})
)
